import { TextField } from "@mui/material"
import { findFieldByServicePath } from "@tc/field-anchor"
import { productPaginationActions } from "@tc/ui-dfe/hooks/config/useProductPagination"
import { SearchParams } from "@tc/typesense"
import { useFormSearchData } from "@tc/ui-dfe/hooks/useFormSearch"
import { useRecipe } from "@tc/ui-dfe/hooks/useRecipe"
import { FormSearchProps, SearchParameters } from "@tc/ui-dfe/utils/types"
import {
  CollectionSetting,
  getCollectionDefaultSettings,
  useTypesenseClient,
} from "@tc/ui-typesense-client"
import get from "lodash/get"
import { ErrorInfo, useEffect, useMemo, useRef, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import {
  ControllerRenderProps,
  FieldError,
  FieldValues,
  Path,
  useController,
  useWatch,
} from "react-hook-form"
import { InstantSearch } from "react-instantsearch"
import { FormSearchUI } from "./FormSearchBase"
import { validateTypesenseFilter } from "./filterUtils"

const TYPESENSE_KEYS: (keyof SearchParams)[] = [
  "q",
  "query_by",
  "query_by_weights",
  "filter_by",
  "sort_by",
  "facet_by",
  "page",
  "per_page",
  "highlight_full_fields",
  "highlight_fields",
  "pinned_hits",
  "hidden_hits",
  "prefix",
  "num_typos",
  "use_cache",
]

const pickTypesenseParams = (
  params: Record<string, unknown>,
): Partial<SearchParams> =>
  Object.fromEntries(
    TYPESENSE_KEYS.map((key) => [key, params[key]]).filter(
      ([, value]) => value !== undefined,
    ),
  ) as Partial<SearchParams>

const getDependencyValue = (value: unknown): string | null => {
  if (!value) return null
  if (typeof value === "string") return value
  if (Array.isArray(value)) {
    const ids = value
      .map((item) =>
        typeof item === "object" && item !== null
          ? (item as Record<string, unknown>).id
          : item,
      )
      .filter((id): id is string => typeof id === "string")
    return ids.length > 0 ? ids.join(",") : null
  }
  if (typeof value === "object" && value !== null) {
    const obj = value as Record<string, unknown>
    return (obj.id as string) || null
  }
  return null
}

export const FormSearch = <
  TFieldValues extends FieldValues,
  TDoc extends Record<string, unknown> = Record<string, unknown>,
>(
  props: FormSearchProps<TFieldValues, TDoc>,
) => {
  const {
    field,
    formState: { errors },
  } = useController({
    name: props.name,
    control: props.control,
    rules: props.rules,
    defaultValue: props.defaultValue,
  })

  const {
    collectionName,
    queryAttributes,
    precondition,
    orderBy,
    filter_by,
    dependOn,
    dependencyFilterKey,
    includeNotApplicable,
  } = props

  const watchedValue = useWatch({
    control: props.control,
    name: dependOn as Path<TFieldValues>,
    disabled: !dependOn,
  })

  const depValue = getDependencyValue(watchedValue)

  const searchBoxRefineRef = useRef<((query: string) => void) | null>(null)

  useEffect(() => {
    if (dependOn && depValue && searchBoxRefineRef.current) {
      searchBoxRefineRef.current("*")
    }
  }, [depValue, dependOn, props.name])

  const searchParams = useMemo(() => {
    const defaultSettings = (getCollectionDefaultSettings(collectionName) ||
      {}) as CollectionSetting
    const finalQueryAttributes =
      queryAttributes || defaultSettings.queryAttributes || ".*"

    const params: Partial<SearchParameters> & Record<string, unknown> = {
      ...defaultSettings,
    }

    const weights = (finalQueryAttributes || "")
      .split(",")
      .filter((weight) => !!weight.length)
      .map(() => "1")
      .join(",")
    params.query_by = finalQueryAttributes
    params.query_by_weights = weights

    const filters: string[] = []

    if (filter_by) {
      filters.push(filter_by)
    }

    if (dependOn && depValue && dependencyFilterKey) {
      // When includeNotApplicable is true, include both the selected dependency value
      // AND 'NOT_APPLICABLE' items. Client-side filtering in useFormSearchData will
      // then remove NOT_APPLICABLE if specific items exist for the selected parent.
      const depFilter = includeNotApplicable
        ? `${dependencyFilterKey}:=[${depValue},NOT_APPLICABLE]`
        : `${dependencyFilterKey}:=${depValue}`
      filters.push(depFilter)
    }

    if (precondition) {
      const preconditionFilters = Array.isArray(precondition)
        ? precondition
        : [precondition]

      preconditionFilters.forEach((condition) => {
        const operator = condition.operator ?? "="
        let value = condition.value

        if (typeof value === "string") {
          if (
            value.includes(" ") ||
            value.includes(":") ||
            value.includes("&")
          ) {
            value = `"${value.replace(/"/g, '\\"')}"`
          }
        }

        const filter = `${condition.attribute}:${operator}${value}`
        if (validateTypesenseFilter(filter)) {
          filters.push(filter)
        }
      })
    }

    if (filters.length > 0) params.filter_by = filters.join(" && ")
    if (orderBy) params.sort_by = orderBy

    return pickTypesenseParams(params)
  }, [
    collectionName,
    queryAttributes,
    precondition,
    filter_by,
    orderBy,
    dependOn,
    depValue,
    dependencyFilterKey,
    includeNotApplicable,
    props.name,
  ])

  const searchParamsKey = useMemo(() => {
    return `${collectionName}-${JSON.stringify(searchParams)}`
  }, [collectionName, searchParams])

  const recipe = useRecipe()
  const submissionLayout = recipe?.recipe?.meta?.submissionLayout as
    | "AP_EXPORT"
    | "AP_TRANSFER"
    | "PHYTO_EXPORT"

  const { adapter } = useTypesenseClient({
    searchParams,
    collectionName,
    usePermission:
      props.usePermissions ??
      (submissionLayout
        ? productPaginationActions.cachedFieldLookup(
            field.name,
            findFieldByServicePath,
          )?.searchRepresentation?.[submissionLayout]?.usePermissions
        : undefined),
    context:
      props.context ??
      (submissionLayout === "AP_TRANSFER"
        ? "transfers"
        : submissionLayout === "AP_EXPORT" ||
            submissionLayout === "PHYTO_EXPORT"
          ? "exports"
          : "generic"),
  })

  // Field with value: init typesense immediately
  // Empty field: init typesense on hover
  // Exception: consignor fields mount immediately for auto-select
  const hasValue =
    !!field.value && !(Array.isArray(field.value) && field.value.length === 0)
  const isConsignorField =
    props?.id === "consignorId" ||
    props?.id === "consignor" ||
    props?.id === "Organisation" ||
    props?.id?.includes("_party_consignor_identifier")
  const [mounted, setMounted] = useState(hasValue || isConsignorField)

  useEffect(() => {
    if (hasValue && !mounted) setMounted(true)
  }, [hasValue, mounted])

  const handleError = (error: Error, info: ErrorInfo) => {
    console.error(
      "FormSearch ErrorBoundary:",
      error?.message?.replace(/[\r\n]/g, ""),
      info?.componentStack?.replace(/[\r\n]/g, ""),
    )
  }

  const error = get(errors, props.name) as FieldError | undefined

  if (!adapter?.searchClient) {
    return (
      <TextField
        disabled
        label={props.label}
        placeholder="Search not available"
        sx={{
          "& .Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
            borderColor: "forest.main",
          },
          "& .Mui-error .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
          },
        }}
      />
    )
  }

  // Empty field not yet activated: render same FormSearchUI with empty data (no blink)
  if (!mounted) {
    return (
      <div
        onMouseEnter={() => setMounted(true)}
        onFocus={() => setMounted(true)}
        style={{ width: "100%" }}
      >
        <FormSearchUI
          props={props}
          data={{
            isLoading: false,
            options: [],
            selectedValue: [],
            inputValue: "",
            showMore: () => {},
            isBulkValidating: false,
            bulkValidateRef: { current: null },
            setIsBulkValidating: () => {},
            skipNextFetchRef: { current: false },
          }}
          handlers={{
            handleSelectionChange: async () => {},
            handleInputChange: () => {},
          }}
          error={error}
        />
      </div>
    )
  }

  // Mounted: full InstantSearch with real data
  return (
    <ErrorBoundary
      FallbackComponent={() => <div>Search not available</div>}
      onError={handleError}
    >
      <InstantSearch
        key={searchParamsKey}
        searchClient={adapter.searchClient}
        indexName={collectionName}
      >
        <FormSearchController
          {...props}
          field={field}
          error={error}
          searchBoxRefineRef={searchBoxRefineRef}
        />
      </InstantSearch>
    </ErrorBoundary>
  )
}

function FormSearchController<
  TFieldValues extends FieldValues,
  TDoc extends Record<string, unknown> = Record<string, unknown>,
>(
  props: Omit<FormSearchProps<TFieldValues, TDoc>, "error"> & {
    field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>
    error?: FieldError
    searchBoxRefineRef: React.MutableRefObject<((query: string) => void) | null>
  },
) {
  const { field, error, searchBoxRefineRef, ...restProps } = props

  const { handlers, ...data } = useFormSearchData<TFieldValues, TDoc>({
    field,
    props: restProps as FormSearchProps<TFieldValues, TDoc>,
    searchBoxRefineRef,
  })

  return (
    <FormSearchUI
      props={restProps as FormSearchProps<TFieldValues, TDoc>}
      data={data}
      handlers={handlers}
      error={error}
    />
  )
}
